import { isWithinSafeArea } from '@/features/letterParty/helpers';

import type { LetterQ, Position } from '@/features/letterParty/types';

export const LETTER_CELL_DISTANCE_BUFFER = 2;
export const GRID_PLACEMENT_ATTEMPT_LIMIT = 100;

interface GridArea {
	width: number;
	height: number;
}

interface BaseGridPositionParams {
	gridWidth: number;
	gridHeight: number;
	usedPositions: Set<string>;
	isPositionBlocked?: (position: Position) => boolean;
}

type LimitedGridPositionParams = BaseGridPositionParams & {
	maxAttempts: number;
};

type UnlimitedGridPositionParams = BaseGridPositionParams & {
	maxAttempts?: undefined;
};

type GridPositionPlacementParams = LimitedGridPositionParams | UnlimitedGridPositionParams;

export function getGridCell({ x, y }: Position): Position {
	return {
		x: Math.floor(x + 0.5),
		y: Math.floor(y + 0.5),
	};
}

export function getGridPositionKey(position: Position): string {
	const { x, y } = getGridCell(position);
	return `${x},${y}`;
}

export function getGridPositionColorSeed({ x, y }: Position): number {
	return x * 100 + y;
}

export function reserveGridPosition(usedPositions: Set<string>, position: Position): void {
	usedPositions.add(getGridPositionKey(position));
}

export function reserveGridArea(usedPositions: Set<string>, { width, height }: GridArea): void {
	for (let x = 0; x < width; x++) {
		for (let y = 0; y < height; y++) {
			reserveGridPosition(usedPositions, { x, y });
		}
	}
}

export function reserveSnakePositions(usedPositions: Set<string>, snake: Position[]): void {
	snake.forEach((segment) => reserveGridPosition(usedPositions, segment));
}

/**
 * Keeps spawned items away from the leader's immediate approach area:
 * the same column above/below the leader, plus the cells to its right.
 * The leader's own cell is reserved by reserveSnakePositions.
 */
export function reserveSnakeLeaderBuffer(
	usedPositions: Set<string>,
	snake: Position[],
	bufferSize = LETTER_CELL_DISTANCE_BUFFER,
): void {
	const leader = snake[0] ? getGridCell(snake[0]) : null;

	if (!leader) {
		return;
	}

	for (let dy = -bufferSize; dy <= bufferSize; dy++) {
		if (dy === 0) {
			continue;
		}

		reserveGridPosition(usedPositions, { x: leader.x, y: leader.y + dy });
	}

	for (let dx = 1; dx <= bufferSize; dx++) {
		reserveGridPosition(usedPositions, { x: leader.x + dx, y: leader.y });
	}
}

export function isPositionTooCloseToLetters(
	position: Position,
	placedLetters: LetterQ[],
	minDistance = LETTER_CELL_DISTANCE_BUFFER,
): boolean {
	return placedLetters.some((letterQ) => {
		const dx = Math.abs(letterQ.position.x - position.x);
		const dy = Math.abs(letterQ.position.y - position.y);

		return dx + dy < minDistance;
	});
}

export function isGridPositionBlocked(
	position: Position,
	usedPositions: Set<string>,
	isPositionBlocked?: (position: Position) => boolean,
): boolean {
	return usedPositions.has(getGridPositionKey(position)) || isPositionBlocked?.(position) === true;
}

function findAvailableGridPositionInternal({
	gridWidth,
	gridHeight,
	usedPositions,
	maxAttempts,
	isPositionBlocked,
}: GridPositionPlacementParams): Position | null {
	let attempts = 0;

	while (maxAttempts == null || attempts < maxAttempts) {
		const position = {
			x: Math.floor(Math.random() * gridWidth),
			y: Math.floor(Math.random() * gridHeight),
		};

		attempts++;

		if (!isGridPositionBlocked(position, usedPositions, isPositionBlocked)) {
			return position;
		}
	}

	return null;
}

export function placeAvailableGridPosition(params: LimitedGridPositionParams): Position | null;
export function placeAvailableGridPosition(params: UnlimitedGridPositionParams): Position;
export function placeAvailableGridPosition(params: GridPositionPlacementParams): Position | null {
	const position = findAvailableGridPositionInternal(params);

	if (position) {
		reserveGridPosition(params.usedPositions, position);
	}

	return position;
}

export function isBlockedBySafeArea(position: Position): boolean {
	return isWithinSafeArea(position);
}
